<script setup lang="ts">
import { ref, watch } from 'vue';
import { cloneDeep } from 'lodash';

import { SliderFilterState } from '@apps/main/new-desktop/modules/filters';

import { exhaustiveGuard } from '@libs/utils';

import { FlightsFilterType } from './FlightsFilters.model';
import type {
  FlightsFilters,
  FlightsFilterEvents,
} from './FlightsFilters.model';
import FlightsFiltersView from './FlightsFiltersView.vue';

interface FlightsFiltersLogicProps {
  data: FlightsFilters;
  shouldUpdateOnClick?: boolean;
}

const props = defineProps<FlightsFiltersLogicProps>();

interface FlightsFiltersLogicEmits {
  (eventName: 'trigger-action', context: FlightsFilterEvents): void;
  (eventName: 'new-actions', context: FlightsFilterEvents[]): void;
}

const emit = defineEmits<FlightsFiltersLogicEmits>();

const userFilters = ref<FlightsFilters | null>(null);

function handleAction(action: FlightsFilterEvents): void {
  if (props.shouldUpdateOnClick) {
    updateFilter(action);
    emit('new-actions', filterEventsActions);
  } else {
    emit('trigger-action', action);
  }
}

const filterEventsActions: FlightsFilterEvents[] = [];

function storeFilterAction(action: FlightsFilterEvents): void {
  if (action.filterType !== FlightsFilterType.Carriers) {
    const indexOfAction = filterEventsActions.findIndex(
      (filterEventAction) => filterEventAction.filterType === action.filterType
    );

    if (indexOfAction !== -1) {
      filterEventsActions[indexOfAction] = action;
    } else {
      filterEventsActions.push(action);
    }
  } else {
    const indexOfAction = filterEventsActions.findIndex(
      (filterEventAction) =>
        filterEventAction.filterType === action.filterType &&
        filterEventAction.data.selected === action.data.selected
    );

    if (indexOfAction !== -1) {
      filterEventsActions.splice(indexOfAction, 1);
    } else {
      filterEventsActions.push(action);
    }
  }
}

function updateFilter(action: FlightsFilterEvents): void {
  storeFilterAction(action);
  updateModalFilterVaules(action);
}

function updateModalFilterVaules(action: FlightsFilterEvents): void {
  switch (action.filterType) {
    case FlightsFilterType.ArrivalTime: {
      const filter = userFilters.value?.arrivalTime.filters[0];

      if (filter) {
        filter.options.initialValue = action.data.range;
      }

      break;
    }
    case FlightsFilterType.Carriers: {
      const filters = userFilters.value?.carriers.data[0]?.filters;

      if (filters) {
        const selected = action.data.selected;
        const filterToUpdate = filters?.find(
          (filter) => filter.options.code === selected
        );

        if (filterToUpdate) {
          filterToUpdate.options.checked = !filterToUpdate.options.checked;
        }
      }

      break;
    }

    case FlightsFilterType.DepartureTime: {
      const filter = userFilters.value?.departureTime.filters[0];

      if (filter) {
        filter.options.initialValue = action.data.range;
      }

      break;
    }
    case FlightsFilterType.NumberOfStops: {
      const filter = userFilters.value?.numberOfStops.filters[0];

      if (filter) {
        filter.selected = action.data.selected;
      }

      break;
    }
    case FlightsFilterType.Price: {
      const filter = userFilters.value?.price.filters[0];

      if (filter && filter.state === SliderFilterState.Default) {
        filter.options.initialValue = action.data.value;
      }

      break;
    }
    case FlightsFilterType.TravelDuration: {
      const filter = userFilters.value?.travelDuration.filters[0];

      if (filter && filter.state === SliderFilterState.Default) {
        filter.options.initialValue = action.data.value;
      }

      break;
    }
    default:
      exhaustiveGuard(action, `filterType not supported`);
  }
}

function clearStoredActions(): void {
  filterEventsActions.length = 0;
}

watch(
  () => props.data,
  () => {
    userFilters.value = cloneDeep(props.data);
    clearStoredActions();
  },
  { immediate: true }
);
</script>

<template>
  <FlightsFiltersView
    v-if="userFilters"
    :data="userFilters"
    @update="handleAction"
  />
</template>
